import { z } from 'zod';
import type { GymModuleKey } from '@bulk/types';
import { idSchema } from './common';

export const SETUP_SECTIONS = ['foundation', 'hosting', 'domain', 'email', 'sms', 'payments', 'doors', 'site', 'launch'] as const;
export type SetupSection = (typeof SETUP_SECTIONS)[number];

export const SETUP_SECTION_TITLES: Record<SetupSection, string> = {
  foundation: 'The gym in Bulk',
  hosting: 'Hosting',
  domain: 'Domain and DNS',
  email: 'Email',
  sms: 'Text messages',
  payments: 'Online payments',
  doors: 'Doors and turnstiles',
  site: 'Website and legal pages',
  launch: 'Launch',
};

export type SetupStepKind = 'DETECTED' | 'MANUAL';

/** Values every step can name as `{key}`, next to the ones it lists itself. */
export const SETUP_UNIVERSAL_FACTS = ['gymName', 'gymId', 'slug', 'baseDomain', 'apiUrl'] as const;

export interface SetupChoice {
  value: string;
  label: string;
}

export interface SetupStepDefinition {
  /** Bulk reads the answer from what it holds (`DETECTED`), or a person records it because Bulk cannot see it (`MANUAL`). */
  kind: SetupStepKind;
  section: SetupSection;
  required: boolean;
  /** The values the step's instructions may name as `{key}`; content that names any other fails its test. */
  facts: readonly string[];
  /** Steps that record which way the gym goes: the value chosen decides which later steps apply. */
  choices?: readonly SetupChoice[];
  /** Steps that must be done before this one can be; shown as what it is waiting for, never enforced. */
  after?: readonly string[];
  /** Hidden while Bulk has this module off for the gym. */
  module?: GymModuleKey;
}

const step = (definition: SetupStepDefinition): SetupStepDefinition => definition;

/**
 * Every step of a gym's setup, in the order to do them. Which steps apply to one gym (its hosting, its email sender, whether it
 * wants texts, which modules are on) is decided in `packages/core/src/services/setup-guide.ts`; this is the part the web app shares.
 */
export const SETUP_STEPS = {
  intake: step({ kind: 'MANUAL', section: 'foundation', required: false, facts: ['slug'] }),
  profile: step({ kind: 'DETECTED', section: 'foundation', required: true, facts: [] }),
  owner: step({ kind: 'DETECTED', section: 'foundation', required: true, facts: ['ownerEmail'] }),
  branches: step({ kind: 'DETECTED', section: 'foundation', required: true, facts: [] }),
  plans: step({ kind: 'DETECTED', section: 'foundation', required: true, facts: [] }),
  staff: step({ kind: 'DETECTED', section: 'foundation', required: false, facts: [] }),
  practice: step({ kind: 'DETECTED', section: 'foundation', required: false, facts: [], module: 'practice' }),

  hosting_choice: step({
    kind: 'MANUAL',
    section: 'hosting',
    required: true,
    facts: [],
    choices: [
      { value: 'shared', label: 'Shared: one deployment for every gym (the default)' },
      { value: 'dedicated', label: 'Dedicated: its own deployment, database and keys' },
    ],
  }),
  shared_base_domain: step({ kind: 'DETECTED', section: 'hosting', required: false, facts: ['baseDomain'], after: ['hosting_choice'] }),
  dedicated_agreement: step({ kind: 'MANUAL', section: 'hosting', required: true, facts: ['slug'], after: ['hosting_choice'] }),
  dedicated_supabase: step({ kind: 'MANUAL', section: 'hosting', required: true, facts: ['slug'], after: ['dedicated_agreement'] }),
  dedicated_railway: step({ kind: 'MANUAL', section: 'hosting', required: true, facts: ['slug'], after: ['dedicated_supabase'] }),
  dedicated_variables: step({ kind: 'MANUAL', section: 'hosting', required: true, facts: ['slug', 'baseDomain'], after: ['dedicated_railway'] }),
  dedicated_domains: step({ kind: 'MANUAL', section: 'hosting', required: true, facts: ['baseDomain'], after: ['dedicated_variables'] }),
  dedicated_health: step({ kind: 'MANUAL', section: 'hosting', required: true, facts: ['slug'], after: ['dedicated_domains'] }),

  domain_choice: step({ kind: 'MANUAL', section: 'domain', required: true, facts: ['slug'] }),
  domain_added: step({ kind: 'DETECTED', section: 'domain', required: true, facts: ['hostname'], after: ['domain_choice'] }),
  domain_dns: step({ kind: 'MANUAL', section: 'domain', required: true, facts: ['hostname', 'txtName', 'txtValue', 'cnameName', 'cnameTarget'], after: ['domain_added'] }),
  domain_verified: step({ kind: 'DETECTED', section: 'domain', required: true, facts: ['hostname', 'txtName', 'txtValue'], after: ['domain_dns'] }),
  domain_tls: step({ kind: 'MANUAL', section: 'domain', required: true, facts: ['hostname'], after: ['domain_verified'] }),
  domain_staff_hosts: step({ kind: 'MANUAL', section: 'domain', required: false, facts: ['hostname', 'signInByAddress'], after: ['domain_tls'] }),
  domain_primary: step({ kind: 'DETECTED', section: 'domain', required: true, facts: ['hostname'], after: ['domain_tls'] }),
  domain_redirects: step({ kind: 'MANUAL', section: 'domain', required: false, facts: ['hostname'], after: ['domain_primary'] }),

  email_choice: step({
    kind: 'MANUAL',
    section: 'email',
    required: true,
    facts: [],
    choices: [
      { value: 'bulk', label: 'Bulk’s own sender, for now' },
      { value: 'resend', label: 'The gym’s own Resend account' },
      { value: 'smtp', label: 'The gym’s own mail server (SMTP)' },
    ],
  }),
  email_platform_sender: step({ kind: 'DETECTED', section: 'email', required: true, facts: ['platformProvider'], after: ['email_choice'] }),
  email_account: step({ kind: 'MANUAL', section: 'email', required: true, facts: ['provider'], after: ['email_choice'] }),
  email_dns: step({ kind: 'MANUAL', section: 'email', required: true, facts: ['provider', 'senderDomain'], after: ['email_account'] }),
  email_credentials: step({ kind: 'DETECTED', section: 'email', required: true, facts: ['provider'], after: ['email_account'] }),
  email_tested: step({ kind: 'DETECTED', section: 'email', required: true, facts: ['provider', 'lastChecked', 'lastError'], after: ['email_credentials'] }),

  sms_decision: step({ kind: 'MANUAL', section: 'sms', required: false, facts: [] }),
  sms_account: step({ kind: 'MANUAL', section: 'sms', required: true, facts: [], after: ['sms_decision'] }),
  sms_sender: step({ kind: 'MANUAL', section: 'sms', required: true, facts: ['sender'], after: ['sms_account'] }),
  sms_credentials: step({ kind: 'DETECTED', section: 'sms', required: true, facts: ['sender'], after: ['sms_sender'] }),
  sms_tested: step({ kind: 'DETECTED', section: 'sms', required: true, facts: ['sender', 'lastChecked', 'lastError'], after: ['sms_credentials'] }),
  sms_delivery_reports: step({ kind: 'MANUAL', section: 'sms', required: false, facts: ['deliveryUrl'], after: ['sms_credentials'] }),
  sms_optout: step({ kind: 'DETECTED', section: 'sms', required: false, facts: ['optOut'], after: ['sms_credentials'], module: 'campaigns' }),
  sms_consent: step({ kind: 'MANUAL', section: 'sms', required: false, facts: [], after: ['sms_tested'], module: 'campaigns' }),

  payments_bank: step({
    kind: 'MANUAL',
    section: 'payments',
    required: true,
    facts: [],
    choices: [
      { value: 'tbc', label: 'TBC Bank' },
      { value: 'bog', label: 'Bank of Georgia' },
    ],
  }),
  payments_credentials: step({ kind: 'DETECTED', section: 'payments', required: true, facts: ['provider'], after: ['payments_bank'] }),
  payments_callback: step({ kind: 'MANUAL', section: 'payments', required: true, facts: ['webhookUrl'], after: ['payments_credentials'] }),
  payments_connection: step({ kind: 'DETECTED', section: 'payments', required: true, facts: ['provider', 'lastChecked', 'lastError'], after: ['payments_credentials'] }),
  payments_test_purchase: step({ kind: 'DETECTED', section: 'payments', required: true, facts: ['provider', 'paidCount'], after: ['payments_connection', 'payments_callback'] }),
  payroll_bank: step({ kind: 'DETECTED', section: 'payments', required: false, facts: ['bankFile'], module: 'payroll' }),

  access_survey: step({ kind: 'MANUAL', section: 'doors', required: true, facts: [], module: 'access' }),
  access_agent_config: step({ kind: 'DETECTED', section: 'doors', required: true, facts: [], after: ['access_survey'], module: 'access' }),
  access_agent_online: step({ kind: 'DETECTED', section: 'doors', required: true, facts: ['lastChecked'], after: ['access_agent_config'], module: 'access' }),
  access_readers: step({ kind: 'DETECTED', section: 'doors', required: true, facts: ['readerCount'], after: ['access_agent_online'], module: 'access' }),
  access_shadow: step({ kind: 'MANUAL', section: 'doors', required: true, facts: [], after: ['access_readers'], module: 'access' }),
  access_cutover: step({ kind: 'MANUAL', section: 'doors', required: false, facts: [], after: ['access_shadow'], module: 'access' }),
  access_harvest: step({ kind: 'DETECTED', section: 'doors', required: false, facts: ['collected', 'promoted'], after: ['access_agent_online'], module: 'access' }),

  site_design: step({ kind: 'DETECTED', section: 'site', required: false, facts: ['gymId'] }),
  branding: step({ kind: 'DETECTED', section: 'site', required: false, facts: [] }),
  site_copy: step({ kind: 'MANUAL', section: 'site', required: true, facts: [], after: ['site_design'] }),
  site_published: step({ kind: 'DETECTED', section: 'site', required: true, facts: [], after: ['site_copy'] }),
  legal_published: step({ kind: 'DETECTED', section: 'site', required: true, facts: ['publishedCount', 'pageCount'] }),
  legal_reviewed: step({ kind: 'MANUAL', section: 'site', required: true, facts: [], after: ['legal_published'] }),
  menu_added: step({ kind: 'DETECTED', section: 'site', required: false, facts: [], module: 'menu' }),
  classes_added: step({ kind: 'DETECTED', section: 'site', required: false, facts: [], module: 'classes' }),
  trainers_added: step({ kind: 'DETECTED', section: 'site', required: false, facts: [], module: 'trainers' }),

  data_migrated: step({ kind: 'MANUAL', section: 'launch', required: false, facts: [] }),
  staff_trained: step({ kind: 'MANUAL', section: 'launch', required: true, facts: [] }),
  activated: step({ kind: 'DETECTED', section: 'launch', required: true, facts: ['blockers'] }),
  first_sale: step({ kind: 'DETECTED', section: 'launch', required: true, facts: ['paidCount'], after: ['activated'] }),
  handover: step({ kind: 'MANUAL', section: 'launch', required: true, facts: [], after: ['first_sale'] }),
} as const satisfies Record<string, SetupStepDefinition>;

export type SetupStepKey = keyof typeof SETUP_STEPS;
export const SETUP_STEP_KEYS = Object.keys(SETUP_STEPS) as SetupStepKey[];

export const setupStepDefinition = (key: SetupStepKey): SetupStepDefinition => SETUP_STEPS[key];

export const SETUP_STATUSES = ['DONE', 'STARTED', 'WAITING', 'TODO', 'SKIPPED'] as const;
export type SetupStatus = (typeof SETUP_STATUSES)[number];

/** What a person may record on a step that only they can see. `TODO` takes the record away. */
export const SETUP_RECORDS = ['DONE', 'WAITING', 'SKIPPED', 'TODO'] as const;

const stepKeySchema = z.enum(SETUP_STEP_KEYS as [SetupStepKey, ...SetupStepKey[]]);

export const setupConfirmationSchema = z.object({
  status: z.enum(['DONE', 'WAITING', 'SKIPPED']),
  note: z.string().nullable(),
  value: z.string().nullable(),
  by: z.string().nullable().describe('Who recorded it, as their email'),
  at: z.string(),
});
export type SetupConfirmationDto = z.infer<typeof setupConfirmationSchema>;

export const setupStepSchema = z.object({
  key: stepKeySchema,
  section: z.enum(SETUP_SECTIONS),
  kind: z.enum(['DETECTED', 'MANUAL']),
  status: z.enum(SETUP_STATUSES),
  required: z.boolean(),
  detail: z.string().nullable().describe('What Bulk found, in a sentence'),
  facts: z.record(z.string(), z.string()).describe('The values the instructions name'),
  blockedBy: z.array(stepKeySchema).describe('Earlier steps that are not finished yet'),
  confirmation: setupConfirmationSchema.nullable(),
});
export type SetupStepDto = z.infer<typeof setupStepSchema>;

export const setupGuideSchema = z.object({
  gymId: idSchema,
  gymName: z.string(),
  hosting: z.enum(['shared', 'dedicated']).nullable(),
  steps: z.array(setupStepSchema),
  total: z.number().int(),
  finished: z.number().int().describe('Steps done or skipped'),
  requiredTotal: z.number().int(),
  requiredFinished: z.number().int(),
  next: z.array(stepKeySchema).describe('The first required steps still open that nothing blocks'),
});
export type SetupGuideDto = z.infer<typeof setupGuideSchema>;

export const setupProgressSchema = z.object({ requiredTotal: z.number().int(), requiredFinished: z.number().int() });
export type SetupProgressDto = z.infer<typeof setupProgressSchema>;

export const recordSetupStepSchema = z.object({
  status: z.enum(SETUP_RECORDS),
  note: z.string().trim().max(500).nullish(),
  value: z.string().trim().max(60).nullish(),
});
export type RecordSetupStepInput = z.infer<typeof recordSetupStepSchema>;

export const setupStepParamsSchema = z.object({ step: stepKeySchema });

export const recordedSetupStepSchema = z.object({ step: stepKeySchema, status: z.enum(SETUP_STATUSES) });

